import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { isStageT } from "../../utils/storebrand-staging";
import { AnyEngagement } from "../engagements.service";
import { FmsService } from "../fms.service";
import { AnalyticsEvent, EventCategory, EventKey } from "./event.payloads";

interface CustomerOrg {
  orgName: string;
  orgNumbers: string[];
}

interface CustomerOrgNumbers {
  organizations: CustomerOrg[];
}

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  orgNumbers$: Observable<CustomerOrgNumbers> =
    this.fmsService.translateAsync<CustomerOrgNumbers>("engagement.orgNumbers");

  constructor(private readonly fmsService: FmsService) {}

  public pushEvent(event: AnalyticsEvent): void {
    if (this.hasAnalyticsUserConsent() || isStageT()) {
      (window.dataLayer || []).push({
        ...event,
      });
    }
  }

  /**
   * Track user engagements - used to better understand our users -
   * Organization numbers are tracked the first time a session starts -
   * Session storage is used to ensure unique sessions. Analytics consent is evaluated
   * @param engagements
   * @returns void
   */
  public trackUserEngagements(engagements: AnyEngagement[]): void {
    if (this.hasAnalyticsUserConsent() && !sessionStorage.getItem("engagements") && engagements) {
      const filteredEngagementNames = engagements
        .filter((eng) => !!eng.getEmployerName())
        .map((engagement) => engagement.getEmployerName()) as string[];

      filteredEngagementNames.forEach((name) => {
        const event = this.createUniversalEvent(EventCategory.UserMetaInformation, EventKey.UserEngagements, name);
        this.pushEvent(event);
      });

      sessionStorage.setItem("engagements", "tracked");
    }
  }

  private readonly hasAnalyticsUserConsent = (): boolean => {
    const cookieConsents = JSON.parse(localStorage.getItem("ot_userConsent") ?? "[]");
    return cookieConsents.includes("analytics") && this.isStorebrandDomain();
  };

  /**
   * Create a Google Analytics universal event - used for custom tracking without altering GA setup
   * @param {EventCategory} eventCategory - EventCategory type
   * @param {EventKey} eventAction - EventKey type
   * @param {string} eventLabel - contains the data to be tracked
   * @returns {AnalyticsEvent}
   */
  private createUniversalEvent(eventCategory: string, eventAction: string, eventLabel: string): AnalyticsEvent {
    return {
      event: "universal-event",
      eventModel: {
        event_category: eventCategory,
        event_action: eventAction,
        event_label: eventLabel,
      },
    };
  }
  private readonly isStorebrandDomain = (): boolean => location.hostname === "www.storebrand.no";
}
